import { localizeWorkspaceError } from '@/lib/workspace-error-copy';
import { useWorkspaceText, workspaceTextFor } from './interface-copy';
import { useNamespaceCopy } from '@/i18n/ns';
import runtimeCopy from '@/i18n/ns/workspace-knowledge-runtime';
import type { ReactNode } from 'react';
import { splitDomMetadata, type DomMetadata } from './dom-metadata';
import { WorkspaceIcon, type WorkspaceIconName } from './icons';

export function PageHeading({
  eyebrow,
  title,
  description,
  children,
  ...props
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  children?: ReactNode;
} & DomMetadata) {
  return (
    <header className="ws-page-heading" {...splitDomMetadata(props).metadata}>
      <div>
        {eyebrow && <div className="ws-eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {children && <div className="ws-actions">{children}</div>}
    </header>
  );
}
/** A page's way to start its work in a new chat: "+ Create in chat" in its heading (Skills, Apps), or with a trailing
 * arrow as its empty state's next step ("Create your first Skill →"). */
export function StartInChatButton({
  label,
  onClick,
  next = false,
}: {
  label: string;
  onClick: () => void;
  next?: boolean;
}) {
  return (
    <button className="ws-button primary" onClick={onClick}>
      {!next && <WorkspaceIcon name="plus" size={16} />}
      {label}
      {next && <WorkspaceIcon name="arrow" size={14} />}
    </button>
  );
}
/** A new object's canvas before the chat has made anything: what to describe, and where it will appear. */
export function NewObjectCanvas({ title, body }: { title: string; body: string }) {
  return (
    <div className="ws-canvas-body">
      <h1>{title}</h1>
      <p className="ws-muted">{body}</p>
    </div>
  );
}
export function EmptyState({
  icon = 'files',
  title,
  description,
  children,
}: {
  icon?: WorkspaceIconName;
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <section className="ws-empty">
      <span className="ws-empty-symbol">
        <WorkspaceIcon name={icon} size={30} />
      </span>
      <h2>{title}</h2>
      <p>{description}</p>
      {children && <div className="ws-actions">{children}</div>}
    </section>
  );
}
export function LoadingState({ label }: { label?: string }) {
  const copy = useNamespaceCopy(runtimeCopy);
  return (
    <div className="ws-loading" role="status">
      <span className="ws-skeleton" />
      <span className="ws-skeleton" />
      <span className="ws-skeleton" />
      <p>{label ?? copy.loadingWorkspace}</p>
    </div>
  );
}
export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { text: uiText, rich: uiRich, locale } = useWorkspaceText();
  return (
    <div className="ws-error" role="alert">
      <p>{localizeWorkspaceError(message, locale)}</p>
      {onRetry && (
        <button className="ws-text-button" onClick={onRetry}>
          {uiRich('Try again {value1}', { value1: <WorkspaceIcon name="arrow" size={14} /> })}
        </button>
      )}
    </div>
  );
}
/** One quiet line for a part that could not be read, with Try again only when there is something to retry. */
export function InlineError({
  message,
  onRetry,
  testId,
}: {
  message: string;
  onRetry?: () => void;
  testId?: string;
}) {
  const { text: uiText, rich: uiRich, locale } = useWorkspaceText();
  return (
    <p className="ws-inline-error" role="status" data-testid={testId}>
      <span>{localizeWorkspaceError(message, locale)}</span>
      {onRetry && (
        <button type="button" className="ws-text-button" onClick={onRetry}>
          {uiText('Try again')}
        </button>
      )}
    </p>
  );
}
export function dateLabel(value?: string | number | null, locale?: string) {
  if (!value) return workspaceTextFor(locale ?? 'en')('Not recorded');
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat(locale ?? 'en', { dateStyle: 'medium' }).format(date)
    : workspaceTextFor(locale ?? 'en')('Not recorded');
}
export function sizeLabel(value: number) {
  return value < 1024
    ? `${value} B`
    : value < 1024 * 1024
      ? `${Math.ceil(value / 1024)} KB`
      : `${(value / 1024 / 1024).toFixed(1)} MB`;
}
